/**
 * Écran « Sous-postes » : le détail des postes budgétaires dans lequel les
 * écritures sont classées. La description de chaque sous-poste est le critère
 * que lit Jev ; l'administration peut la préciser, créer des sous-postes et
 * archiver ceux qui ne servent plus.
 */

import { NOMENCLATURE, POSTE_HORS_BUDGET, entier } from '@scc/core';
import { ErreurApi, type Identite } from '../../api.js';
import { badge, enteteSection, noteLecture } from '../../composants.js';
import { h, vider } from '../../dom.js';
import { libellePosteOuHorsBudget } from '../../postes.js';
import {
  attendreFinDePasse,
  creerSousPoste,
  listeSousPostes,
  modifierSousPoste,
  notifierChangement,
  phraseBilan,
  surFinDePasse,
  synchroniserPlanComptable,
  type BilanSynchro,
  type Sens,
  type SousPoste,
} from './api.js';
import { ORIGINES, grouperSousPostesParPoste, libelleCompte, ordonnerArbre } from './comptes.js';
import { avecVerrou, type Verrouillable } from './verrou.js';

const SENS: Readonly<Record<Sens, string>> = {
  recette: 'Entrées',
  depense: 'Sorties',
  tous: 'Tous sens',
};

/**
 * Postes vers lesquels un sous-poste peut être rattaché : ceux de la
 * nomenclature dont la nature s'accorde à son sens (un produit ne va pas vers
 * une charge, ni l'inverse), et toujours « Hors budget ». Même règle que le serveur.
 */
const postesAdmis = (s: SousPoste): readonly { readonly code: string; readonly libelle: string }[] => [
  ...NOMENCLATURE.filter((p) => s.sens === 'tous' || (s.sens === 'recette' ? p.nature === 'produit' : p.nature === 'charge')).map(
    (p) => ({ code: p.code, libelle: p.libelle }),
  ),
  { code: POSTE_HORS_BUDGET, libelle: 'Hors budget' },
];

/** Le libellé et l'état d'un compte appartiennent à Zoho ; son poste et sa description restent à régler ici. */
const estCompteZoho = (s: SousPoste): boolean => s.origine === 'zoho';

/** Bilan d'une synchronisation, en une phrase (migration unique comprise). */
function phraseSynchro(b: BilanSynchro): string {
  const m = b.migration;
  return (
    `Synchronisation terminée — comptes créés : ${entier(b.crees)}, mis à jour : ${entier(b.misAJour)}, archivés : ${entier(b.archives)} ; ` +
    `rattachements à confirmer : ${entier(b.aConfirmer)}.` +
    (m
      ? ` Migration des classements — arbitrages précisés par le compte : ${entier(m.arbitragesPrecises)}, reportés : ${entier(m.arbitragesReportes)} ; ` +
        `écritures remises à classer : ${entier(m.remisAClasser)} ; règles reportées : ${entier(m.reglesReportees)}.`
      : '')
  );
}

/** Identifiant `snake_case` déduit d'un libellé : sans accent, minuscules, 40 caractères au plus. */
function identifiantDepuis(libelle: string): string {
  const base = libelle
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return (/^[a-z]/.test(base) ? base : `sp_${base}`).slice(0, 40).replace(/_+$/g, '');
}

const FORMAT_IDENTIFIANT = /^[a-z][a-z0-9_]{1,40}$/;

export function vueSousPostes(identite: Identite): HTMLElement {
  const admin = identite.role === 'administration';
  const corps = h('div', {}, h('p', { classe: 'note-lecture' }, 'Chargement des sous-postes…'));
  const retour = h('div', { role: 'status' });
  const etatPasse = h('p', { classe: 'note-lecture', role: 'status' });
  let generation = 0;
  let sousPostes: SousPoste[] = [];
  let seulementAConfirmer = false;
  /** Emplacement du bouton « Forcer la synchronisation », proposé quand le plan reçu paraît tronqué. */
  const zoneForcer = h('div', {});
  /**
   * Rappel abonné aux fins de passe et aux changements venus d'autres écrans.
   * Une vue retirée du document (changement de thème) se désabonne au premier appel.
   */
  const rafraichir = (): void => {
    if (!racine.isConnected) {
      desabonner();
      return;
    }
    void charger();
  };
  const desabonner = surFinDePasse(rafraichir);

  const signaler = (texte: string, ok: boolean): void => {
    vider(retour);
    retour.appendChild(h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: ok ? null : 'alert' }, texte));
  };
  const echec = (e: unknown): void => {
    signaler(e instanceof ErreurApi ? e.message : 'Opération refusée.', false);
  };

  /** Archiver ou réactiver change les cibles admises : le serveur reclasse en arrière-plan. */
  const suivreReclassement = async (message: string): Promise<void> => {
    signaler(`${message} Reclassement des écritures en cours…`, true);
    etatPasse.textContent = 'Classement en cours…';
    try {
      const r = await attendreFinDePasse();
      etatPasse.textContent = phraseBilan(r.passe) ?? '';
      signaler(`${message} Reclassement terminé.`, true);
    } catch (e) {
      etatPasse.textContent = '';
      echec(e);
    }
  };

  /** Enregistre un champ modifié en place ; la liste reprend ensuite l'état du serveur. */
  const enregistrerChamp = (s: SousPoste, m: { libelle?: string; description?: string }, champ: Verrouillable): void => {
    void (async () => {
      try {
        await avecVerrou([champ], () => modifierSousPoste(s.id, m));
        signaler(`Sous-poste « ${m.libelle ?? s.libelle} » enregistré.`, true);
        // Le champ montre déjà la valeur enregistrée : la table n'est pas reconstruite sous le curseur.
        // Les autres écrans, eux, sont prévenus.
        notifierChangement(rafraichir);
      } catch (e) {
        echec(e);
        await charger();
      }
    })();
  };

  const basculerArchive = (s: SousPoste, bouton: Verrouillable): void => {
    void (async () => {
      try {
        await avecVerrou([bouton], () => modifierSousPoste(s.id, { archive: !s.archive }));
      } catch (e) {
        echec(e);
        await charger();
        return;
      }
      await charger();
      await suivreReclassement(s.archive ? 'Sous-poste réactivé.' : 'Sous-poste archivé.');
    })();
  };

  /**
   * Après un rechargement, le contrôle qui avait le focus n'existe plus : on le
   * rend à la ligne concernée (la liste des postes, sinon le filtre si le
   * filtre a fait disparaître la ligne).
   */
  const rendreLeFocus = (id: string): void => {
    (document.getElementById(`sp-poste-${id}`) ?? filtre).focus();
  };

  /** Rattache le compte à un autre poste : le changement vaut confirmation. */
  const changerPoste = (s: SousPoste, choix: HTMLSelectElement, bouton: HTMLButtonElement): void => {
    void (async () => {
      try {
        await avecVerrou([choix, bouton], () => modifierSousPoste(s.id, { poste: choix.value }));
      } catch (e) {
        echec(e);
        await charger();
        rendreLeFocus(s.id);
        return;
      }
      signaler(`« ${libelleCompte(s)} » rattaché au poste « ${libellePosteOuHorsBudget(choix.value)} ».`, true);
      await charger();
      rendreLeFocus(s.id);
      notifierChangement(rafraichir);
    })();
  };

  const confirmerPoste = (s: SousPoste, bouton: Verrouillable): void => {
    void (async () => {
      try {
        await avecVerrou([bouton], () => modifierSousPoste(s.id, { confirmer: true }));
      } catch (e) {
        echec(e);
        await charger();
        rendreLeFocus(s.id);
        return;
      }
      signaler(`Rattachement de « ${libelleCompte(s)} » au poste « ${libellePosteOuHorsBudget(s.poste)} » confirmé.`, true);
      await charger();
      rendreLeFocus(s.id);
      notifierChangement(rafraichir);
    })();
  };

  /**
   * Cellule du poste : liste déroulante (compte Zoho ou manuel) avec un bouton
   * « Enregistrer » explicite — les flèches du clavier changent la valeur d'une
   * liste fermée, elles ne doivent pas écrire —, pastille « proposé » et
   * bouton « Confirmer ».
   */
  const cellulePoste = (s: SousPoste): HTMLElement => {
    const modifiable = admin && (s.origine === 'zoho' || s.origine === 'manuelle');
    const propose = !s.posteConfirme;
    const controles: (HTMLElement | null)[] = [];
    if (modifiable) {
      const admis = postesAdmis(s);
      const liste = h(
        'select',
        { id: `sp-poste-${s.id}`, 'aria-label': `Poste de ${libelleCompte(s)}` },
        // Un poste incohérent avec le sens (donnée antérieure) reste visible plutôt que de laisser la liste vide.
        ...(admis.some((p) => p.code === s.poste)
          ? []
          : [h('option', { value: s.poste, disabled: true }, `${libellePosteOuHorsBudget(s.poste)} (incohérent avec le sens)`)]),
        ...admis.map((p) => h('option', { value: p.code }, p.libelle)),
      );
      liste.value = s.poste;
      const enregistrer = h(
        'button',
        {
          classe: 'btn-action',
          type: 'button',
          id: `sp-enregistrer-${s.id}`,
          disabled: true,
          'aria-label': `Enregistrer le poste de ${libelleCompte(s)}`,
          surClick: () => {
            changerPoste(s, liste, enregistrer);
          },
        },
        'Enregistrer',
      );
      liste.addEventListener('change', () => {
        enregistrer.disabled = liste.value === s.poste;
      });
      controles.push(liste, enregistrer);
    } else {
      controles.push(h('span', {}, libellePosteOuHorsBudget(s.poste)));
    }
    return h(
      'td',
      {},
      h(
        'div',
        { classe: 'actions-arbitrage' },
        ...controles,
        propose ? badge('vigilance', 'proposé') : null,
        propose && admin
          ? h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                'aria-label': `Confirmer le rattachement de ${libelleCompte(s)} au poste ${libellePosteOuHorsBudget(s.poste)}`,
                surClick: (ev) => {
                  confirmerPoste(s, ev.currentTarget as HTMLButtonElement);
                },
              },
              'Confirmer',
            )
          : null,
      ),
    );
  };

  const ligne = (s: SousPoste, profondeur: number): HTMLElement => {
    const idLibelle = `sp-libelle-${s.id}`;
    const idDescription = `sp-description-${s.id}`;
    // Le libellé d'un compte Zoho se lit ici et se modifie dans Zoho Books.
    let nom: HTMLElement;
    if (estCompteZoho(s) || !admin) {
      nom = h('span', { classe: 'compte-libelle' }, s.libelle);
    } else {
      const libelle = h('input', { type: 'text', id: idLibelle, maxlength: 120, 'aria-label': `Libellé de ${s.libelle}` });
      libelle.value = s.libelle;
      libelle.addEventListener('change', () => {
        enregistrerChamp(s, { libelle: libelle.value }, libelle);
      });
      nom = libelle;
    }
    const description = h('textarea', {
      id: idDescription,
      rows: 2,
      maxlength: 600,
      'aria-label': `Description de ${libelleCompte(s)}`,
      placeholder: s.descriptionZoho ? 'Précision pour Jev (à défaut, la description Zoho est lue)' : null,
      disabled: !admin,
    });
    description.value = s.description;
    description.addEventListener('change', () => {
      enregistrerChamp(s, { description: description.value }, description);
    });
    const etat = s.archive ? (estCompteZoho(s) ? 'Archivé dans Zoho' : 'Archivé') : 'Actif';
    return h(
      'tr',
      { classe: s.archive ? 'regle-inactive' : '' },
      h(
        'td',
        {},
        h(
          'div',
          { classe: 'compte', '--profondeur': profondeur },
          s.code !== '' ? h('code', { classe: 'compte-code' }, s.code) : null,
          nom,
          h('span', { classe: `badge-origine origine-${s.origine}` }, ORIGINES[s.origine]),
        ),
      ),
      cellulePoste(s),
      h(
        'td',
        {},
        description,
        s.descriptionZoho ? h('div', { classe: 'note-lecture' }, `Zoho : ${s.descriptionZoho}`) : null,
      ),
      h('td', {}, SENS[s.sens]),
      h('td', {}, etat),
      admin
        ? h(
            'td',
            {},
            // L'archivage d'un compte Zoho suit Zoho : la synchronisation le reporte.
            estCompteZoho(s)
              ? null
              : h(
                  'button',
                  {
                    classe: 'btn-action',
                    type: 'button',
                    'aria-label': `${s.archive ? 'Réactiver' : 'Archiver'} le sous-poste ${s.libelle}`,
                    surClick: (ev) => {
                      basculerArchive(s, ev.currentTarget as HTMLButtonElement);
                    },
                  },
                  s.archive ? 'Réactiver' : 'Archiver',
                ),
          )
        : null,
    );
  };

  /** Dessine l'arborescence à partir des sous-postes déjà chargés (filtre compris). */
  const dessiner = (): void => {
    vider(corps);
    const visibles = seulementAConfirmer ? sousPostes.filter((s) => !s.posteConfirme && !s.archive) : sousPostes;
    if (sousPostes.length === 0) {
      corps.appendChild(noteLecture('Aucun sous-poste.'));
      return;
    }
    if (visibles.length === 0) {
      corps.appendChild(noteLecture('Aucun rattachement à confirmer.'));
      return;
    }
    for (const [poste, membres] of grouperSousPostesParPoste(visibles)) {
      corps.appendChild(
        h(
          'div',
          { classe: 'card' },
          h('h3', {}, libellePosteOuHorsBudget(poste)),
          h(
            'table',
            { classe: 'comp-table tableau-arbitrage tableau-sous-postes' },
            h('caption', { classe: 'invisible' }, `Sous-postes de ${libellePosteOuHorsBudget(poste)}`),
            h(
              'thead',
              {},
              h(
                'tr',
                {},
                h('th', { scope: 'col' }, 'Compte'),
                h('th', { scope: 'col' }, 'Poste'),
                h('th', { scope: 'col' }, 'Description (lue par Jev)'),
                h('th', { scope: 'col' }, 'Sens'),
                h('th', { scope: 'col' }, 'État'),
                admin ? h('th', { scope: 'col' }, 'Action') : null,
              ),
            ),
            h('tbody', {}, ...ordonnerArbre(membres).map((l) => ligne(l.sousPoste, l.profondeur))),
          ),
        ),
      );
    }
  };

  const charger = async (): Promise<void> => {
    const moi = ++generation;
    try {
      const reponse = await listeSousPostes();
      if (moi !== generation) return;
      sousPostes = reponse.sousPostes;
      const nb = sousPostes.filter((s) => !s.posteConfirme && !s.archive).length;
      libelleFiltre.textContent = `Rattachements à confirmer seulement (${entier(nb)})`;
      dessiner();
    } catch (e) {
      if (moi !== generation) return;
      vider(corps);
      corps.appendChild(noteLecture('Les sous-postes n’ont pas pu être chargés.'));
      echec(e);
    }
  };

  // ── Synchronisation avec le plan comptable Zoho ─────────────────────────
  const boutonSynchro = h(
    'button',
    {
      classe: 'btn-action',
      type: 'button',
      surClick: () => {
        synchroniser(false);
      },
    },
    'Synchroniser avec Zoho',
  );

  /** Relit le plan comptable ; un plan qui paraît tronqué est refusé, et peut être forcé après confirmation. */
  const synchroniser = (force: boolean): void => {
    vider(zoneForcer);
    void (async () => {
      // Le bouton reste inactif tant que la passe lancée par la synchronisation n'est pas terminée.
      boutonSynchro.disabled = true;
      try {
        await synchronisation(force);
      } finally {
        boutonSynchro.disabled = false;
      }
    })();
  };

  const synchronisation = async (force: boolean): Promise<void> => {
    let bilan: Awaited<ReturnType<typeof synchroniserPlanComptable>>;
    try {
      bilan = await synchroniserPlanComptable(force);
    } catch (e) {
      echec(e);
      return;
    }
    if ('refuse' in bilan) {
      signaler(bilan.motif, false);
      zoneForcer.appendChild(
        h(
          'button',
          {
            classe: 'btn-action',
            type: 'button',
            surClick: () => {
              if (
                window.confirm(
                  'Forcer la synchronisation malgré un plan comptable qui paraît incomplet ? ' +
                    'Les comptes absents du plan reçu seront archivés.',
                )
              ) {
                synchroniser(true);
              }
            },
          },
          'Forcer la synchronisation',
        ),
      );
      return;
    }
    await charger();
    notifierChangement(rafraichir);
    await suivreReclassement(phraseSynchro(bilan));
  };

  const filtre = h('input', { type: 'checkbox', id: 'sp-filtre-a-confirmer' });
  filtre.addEventListener('change', () => {
    seulementAConfirmer = filtre.checked;
    dessiner();
  });
  const libelleFiltre = h('span', {}, 'Rattachements à confirmer seulement');

  // ── Formulaire « Nouveau sous-poste » ───────────────────────────────────
  const formulaire = (): HTMLElement => {
    const libelle = h('input', { type: 'text', id: 'sp-nouveau-libelle', maxlength: 120 });
    const identifiant = h('input', { type: 'text', id: 'sp-nouveau-id', maxlength: 41 });
    const poste = h(
      'select',
      { id: 'sp-nouveau-poste' },
      ...NOMENCLATURE.map((p) => h('option', { value: p.code }, `${p.libelle} (${p.nature === 'produit' ? 'produit' : 'charge'})`)),
      h('option', { value: POSTE_HORS_BUDGET }, 'Hors budget'),
    );
    const sens = h(
      'select',
      { id: 'sp-nouveau-sens' },
      ...(Object.keys(SENS) as Sens[]).map((s) => h('option', { value: s }, SENS[s])),
    );
    const description = h('textarea', { id: 'sp-nouveau-description', rows: 3, maxlength: 600 });
    let identifiantModifie = false;

    // Le sens suit la nature du poste choisi ; il reste modifiable.
    const accorderSens = (): void => {
      const nature = NOMENCLATURE.find((p) => p.code === poste.value)?.nature;
      sens.value = nature === 'produit' ? 'recette' : nature === 'charge' ? 'depense' : 'tous';
    };
    accorderSens();
    poste.addEventListener('change', accorderSens);
    libelle.addEventListener('input', () => {
      if (!identifiantModifie) identifiant.value = identifiantDepuis(libelle.value);
    });
    identifiant.addEventListener('input', () => {
      identifiantModifie = true;
    });

    const bouton = h('button', { classe: 'btn-action', type: 'button', surClick: () => { creer(); } }, 'Créer le sous-poste');
    const creer = (): void => {
      if (libelle.value.trim() === '') {
        signaler('Indiquez le libellé du sous-poste.', false);
        return;
      }
      if (!FORMAT_IDENTIFIANT.test(identifiant.value)) {
        signaler('Identifiant invalide : lettres minuscules, chiffres et _, commençant par une lettre (2 à 41 caractères).', false);
        return;
      }
      void (async () => {
        try {
          await avecVerrou([bouton, libelle, identifiant, poste, sens, description], () =>
            creerSousPoste({
              id: identifiant.value,
              poste: poste.value,
              libelle: libelle.value.trim(),
              description: description.value.trim(),
              sens: sens.value as Sens,
            }),
          );
        } catch (e) {
          echec(e);
          return;
        }
        signaler(`Sous-poste « ${libelle.value.trim()} » créé.`, true);
        libelle.value = '';
        identifiant.value = '';
        identifiantModifie = false;
        description.value = '';
        await charger();
        notifierChangement(rafraichir);
      })();
    };

    return h(
      'div',
      { classe: 'card' },
      h('h3', {}, 'Nouveau sous-poste'),
      h(
        'div',
        { classe: 'editeur-champs' },
        h('label', {}, 'Libellé', libelle),
        h('label', {}, 'Identifiant', identifiant),
        h('label', {}, 'Poste', poste),
        h('label', {}, 'Sens', sens),
      ),
      h('div', { classe: 'editeur-champs' }, h('label', { classe: 'champ-large' }, 'Description (critère lu par Jev)', description)),
      h('div', { classe: 'barre-actions' }, bouton),
    );
  };

  void charger();

  const racine = h(
    'div',
    { classe: 'page', id: 'page-sous-postes', role: 'region', 'aria-label': 'Sous-postes' },
    h(
      'section',
      {},
      enteteSection(1, 'Sous-postes', 'Le détail des postes budgétaires dans lequel les écritures sont classées'),
      noteLecture(
        'Chaque **compte du plan comptable Zoho** est un sous-poste, rattaché à un poste voté. Le rattachement ' +
          'proposé par la synchronisation reste à confirmer ; le libellé et l’archivage d’un compte se modifient ' +
          'dans Zoho Books. La **description** est le critère que lit Jev pour proposer un classement (à défaut, ' +
          'celle de Zoho) : plus elle est précise, meilleures sont les propositions. Un sous-poste archivé n’est ' +
          'plus proposé mais conserve les écritures déjà classées.',
      ),
      etatPasse,
      retour,
      admin ? h('div', { classe: 'barre-actions' }, boutonSynchro, zoneForcer) : null,
      h('div', { classe: 'barre-actions' }, h('label', { for: 'sp-filtre-a-confirmer' }, filtre, libelleFiltre)),
      corps,
      admin ? formulaire() : null,
    ),
  );
  return racine;
}
